/**
 * /c/:slug/cabinet — Cabinet roster (CHAP-251). Member cards with status badges,
 * titles, committees. Team/admin can edit status / note; others see read-only.
 */
import { useParams } from 'react-router-dom';
import { useRoster, useUpdateMember, type MemberStatus, type RosterMember } from '../hooks/useRoster.js';
import { useUserRoles } from '../hooks/useUserRoles.js';

const STATUS: Record<MemberStatus, { label: string; cls: string; strike?: boolean }> = {
  active: { label: 'Active', cls: 'bg-green-100 text-green-800' },
  resigning: { label: 'Resigning', cls: 'bg-amber-100 text-amber-800' },
  on_leave: { label: 'On leave', cls: 'bg-blue-100 text-blue-800' },
  resigned: { label: 'Resigned', cls: 'bg-gray-200 text-gray-600', strike: true },
};

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?';
}
const AVATAR_COLORS = ['#03244d', '#cc785c', '#4ecdc4', '#f0c040', '#7c3aed', '#0d9488'];

export default function Cabinet() {
  const { slug = '' } = useParams();
  const { data, isLoading, error } = useRoster(slug);
  const { allowedRoles } = useUserRoles();
  const canEdit = allowedRoles.includes('team') || allowedRoles.includes('admin');
  const update = useUpdateMember(slug);

  if (isLoading) return <div className="p-8 text-center text-sm text-neutral">Loading roster…</div>;
  if (error) return <div className="p-8 text-center text-sm text-red-700">{error.message}</div>;
  const roster = data?.roster ?? [];

  return (
    <div className="space-y-4 min-w-0">
      <header className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <h2 className="text-navy text-xl sm:text-2xl font-bold">Cabinet</h2>
        <p className="text-neutral text-sm mt-1">{roster.length} members · governance roster + status.</p>
      </header>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 min-w-0">
        {roster.map((m, i) => (
          <MemberCard key={m.id} m={m} color={AVATAR_COLORS[i % AVATAR_COLORS.length]!} canEdit={canEdit}
            onStatus={(s) => update.mutate({ id: m.id, memberStatus: s })}
            onNote={(n) => { if (n !== (m.statusNote ?? '')) update.mutate({ id: m.id, statusNote: n || null }); }} />
        ))}
        {roster.length === 0 && <p className="text-sm text-neutral">No roster yet.</p>}
      </div>
    </div>
  );
}

function MemberCard({ m, color, canEdit, onStatus, onNote }: {
  m: RosterMember; color: string; canEdit: boolean;
  onStatus: (s: MemberStatus) => void; onNote: (n: string) => void;
}) {
  const st = STATUS[m.memberStatus];
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 min-w-0">
      <div className="flex items-start gap-3 min-w-0">
        <span className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0" style={{ backgroundColor: color }}>
          {initials(m.name)}
        </span>
        <div className="min-w-0 flex-1">
          <p className={`font-semibold text-navy truncate ${st.strike ? 'line-through text-gray-500' : ''}`}>{m.name}</p>
          {m.title && <p className="text-xs text-neutral truncate">{m.title}</p>}
          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
            <span className={`text-[10px] px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
            {m.tier && <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">{m.tier}</span>}
          </div>
        </div>
      </div>
      {m.committees.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {m.committees.map((c) => <span key={c} className="text-[10px] px-1.5 py-0.5 rounded bg-navy/5 text-navy">{c}</span>)}
        </div>
      )}
      {m.appointedDate && <p className="text-[11px] text-neutral mt-2">Appointed {new Date(`${m.appointedDate}T00:00:00`).toLocaleDateString()}</p>}
      {canEdit ? (
        <div className="mt-3 pt-2 border-t border-gray-100 space-y-2">
          <select aria-label={`Member status for ${m.name}`} defaultValue={m.memberStatus} onChange={(e) => onStatus(e.target.value as MemberStatus)} className="w-full text-xs border border-gray-300 rounded px-2 py-1">
            <option value="active">Active</option>
            <option value="resigning">Resigning</option>
            <option value="on_leave">On leave</option>
            <option value="resigned">Resigned</option>
          </select>
          <input aria-label={`Status note for ${m.name}`} defaultValue={m.statusNote ?? ''} placeholder="Status note…" onBlur={(e) => onNote(e.target.value)} className="w-full text-xs border border-gray-300 rounded px-2 py-1" />
        </div>
      ) : (
        m.statusNote && <p className="text-xs text-neutral mt-2 italic">{m.statusNote}</p>
      )}
    </div>
  );
}
